import { forwardRef, useMemo } from 'react';
import { useLocale } from '../../../lib/hooks/use-locale.ts';
import type { ExplorerGetForProductItem } from '../../../server/types/infer-trpc-types.ts';
import type {
    BillingStatus,
    CustomDateRange,
    ExplorerSort,
    ExplorerViewFilters,
    ExplorerViewHandles,
    TargetUnit,
    VisibilityWindow,
} from '../../../server/utils/explorer-utils.ts';
import { api } from '../../../utils/api.ts';
import { ProductLinkCell } from '../../list-views/components/product-link-cell.tsx';
import { useConfigurableExplorerColumns } from '../hooks/use-configurable-explorer-columns';
import { useExplorerSorting } from '../hooks/use-explorer-sorting';
import useRowSelection from '../hooks/use-manage-explorer-filters';
import { usePreventExplorerUnnecissearyRerender } from '../hooks/use-prevent-explorer-unnecisseary-rerender';
import { ExplorerDataTable } from './explorer-data-table';

type Props = {
    hideUnselectedRows?: boolean;
    searchString?: string;
    window: VisibilityWindow;
    customDateRange: CustomDateRange | null;
    filters: ExplorerViewFilters;
    targetUnit: TargetUnit;
    billingStatus: BillingStatus;
    onFiltersChange: (filters: string[]) => void;
    sortingFilter: ExplorerSort | null;
    setSortingFilter: (sortingFilter: ExplorerSort | null) => void;
    yearPeriods: number;
};

export const ExplorerViewProduct = forwardRef<ExplorerViewHandles, Props>(
    (
        {
            hideUnselectedRows = false,
            searchString,
            window,
            customDateRange,
            filters,
            targetUnit,
            billingStatus,
            onFiltersChange,
            sortingFilter,
            setSortingFilter,
            yearPeriods,
        },
        ref,
    ) => {
        const { t } = useLocale();

        const { sorting, sortChangedInInterface } = useExplorerSorting(sortingFilter, setSortingFilter);

        const { filtersWithRerenderProtection } = usePreventExplorerUnnecissearyRerender(
            filters,
            'product',
            hideUnselectedRows,
        );

        const {
            data: dataRaw,
            isLoading,
            isFetching,
            isFetchingNextPage,
            hasNextPage,
            fetchNextPage,
        } = api.platoUser.query.explorer.getForProduct.getListWithFilters.useInfiniteQuery(
            {
                searchString,
                hideUnselectedRows,
                filters: filtersWithRerenderProtection,
                customDateRange,
                billingStatus,
                window,
                sorting: sortingFilter,
                yearPeriods,
            },
            {
                getNextPageParam: (lastPage) => lastPage.nextCursor,
            },
        );

        const columns = useConfigurableExplorerColumns({
            columns: [
                {
                    id: 'name',
                    accessorKey: 'name',
                    headerText: t('header_tab__PRODUCT', {
                        ns: 'explorer',
                    }),
                    generateCell: (row) => <ProductLinkCell erpProductId={row.id} productName={row.name} />,
                    size: 200,
                },
            ],
        });

        const data = useMemo(() => {
            return dataRaw ? dataRaw.pages.flatMap((page) => page.result) : [];
        }, [dataRaw]);

        const { rowSelection, setRowSelection } = useRowSelection<ExplorerGetForProductItem, 'id'>(
            data,
            'id',
            onFiltersChange,
            filters.product,
        );

        return (
            <ExplorerDataTable
                ref={ref}
                data={data}
                columns={columns}
                fetchNextPage={fetchNextPage}
                isFetchingNextPage={isFetchingNextPage}
                isFetching={isFetching}
                hasNextPage={hasNextPage}
                isLoading={isLoading}
                filters={filters}
                window={window}
                billingStatus={billingStatus}
                customDateRange={customDateRange}
                rowSelection={rowSelection}
                setRowSelection={setRowSelection}
                sorting={sorting}
                sortChangedInInterface={sortChangedInInterface}
            />
        );
    },
);
ExplorerViewProduct.displayName = 'ExplorerViewProduct';
